import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Stack, 
  Chip, 
  TextField, 
  Button, 
  MenuItem, 
  Divider, 
  useMediaQuery, 
  useTheme,
  Collapse,
  Paper
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';

// Enhanced Stat Card for Business Metrics - Mobile Optimized
const BusinessStatCard = ({ 
  icon, 
  value, 
  symbol, 
  sign, 
  title, 
  subtitle, 
  description, 
  iconBg, 
  numberColor, 
  trend 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        background: 'white',
        borderRadius: 4,
        p: { xs: 3, sm: 4 },
        textAlign: 'center',
        border: '2px solid transparent',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 200, sm: 240, md: 260 },
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: { xs: 'scale(1.02)', md: 'translateY(-8px)' },
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          borderColor: numberColor + '40',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${numberColor}, ${numberColor}80)`,
        }
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            width: { xs: 50, sm: 55, md: 60 },
            height: { xs: 50, sm: 55, md: 60 },
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: { xs: '0 auto 12px', md: '0 auto 16px' },
            fontSize: { xs: 24, sm: 26, md: 28 },
            background: iconBg,
          }}
        >
          {icon}
        </Box>
        
        <VisibilitySensor partialVisibility offset={{ bottom: 20 }}>
          {({ isVisible }) => (
            <Typography
              variant='h2'
              fontWeight={700}
              fontSize={{ xs: 28, sm: 32, md: 36, lg: 44 }}
              sx={{ 
                color: numberColor,
                mb: 1,
                fontFamily: 'monospace',
                lineHeight: 1
              }}>
              {sign}
              {isVisible ? <CountUp end={value} duration={2} /> : value}
              {symbol}
            </Typography>
          )}
        </VisibilitySensor>

        <Typography
          variant='h6'
          fontSize={{ xs: 14, sm: 15, md: 16 }}
          fontWeight={600}
          sx={{ mb: 0.5, color: '#1a202c', lineHeight: 1.2 }}>
          {title}
        </Typography>
        
        <Typography
          variant='body2'
          fontSize={{ xs: 11, sm: 12, md: 13 }}
          color='text.secondary'
          sx={{ opacity: 0.8, mb: { xs: 1.5, md: 2 }, lineHeight: 1.3 }}>
          {subtitle}
        </Typography>
      </Box>

      <Box
        sx={{
          background: `linear-gradient(135deg, ${numberColor}20, ${numberColor}10)`,
          border: `1px solid ${numberColor}30`,
          borderRadius: 2,
          p: { xs: 1.5, md: 1.5 },
          mt: 'auto'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 600,
            color: numberColor,
            lineHeight: 1.2
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

// ROI Calculator Component - Mobile Optimized
const ROICalculator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    businessType: '',
    customersPerWeek: '',
    hoursPerWeek: '',
    hourlyRate: '',
    responseTime: ''
  });
  const [showResults, setShowResults] = useState(false);
  const [calculatedSavings, setCalculatedSavings] = useState({});
  const [showAdvancedInputs, setShowAdvancedInputs] = useState(false);

  const businessTypes = [
    { value: 'restaurant', label: 'Restaurant/Food Service', multiplier: 1.2 },
    { value: 'retail', label: 'Retail Store', multiplier: 1.0 },
    { value: 'healthcare', label: 'Healthcare/Dental', multiplier: 1.5 },
    { value: 'automotive', label: 'Automotive Service', multiplier: 1.1 },
    { value: 'beauty', label: 'Beauty/Salon', multiplier: 1.0 },
    { value: 'professional', label: 'Professional Services', multiplier: 1.3 },
    { value: 'home', label: 'Home Services', multiplier: 1.2 },
    { value: 'other', label: 'Other', multiplier: 1.0 }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateROI = () => {
    const customers = parseInt(formData.customersPerWeek) || 0;
    const hours = parseInt(formData.hoursPerWeek) || 0;
    const rate = parseInt(formData.hourlyRate) || 25;
    const businessMultiplier = businessTypes.find(b => b.value === formData.businessType)?.multiplier || 1.0;

    // Base calculations
    const weeklyLaborCost = hours * rate;
    const monthlyLaborCost = weeklyLaborCost * 4.33;
    
    // AI saves 85% of customer service time
    const timeSavings = hours * 0.85;
    const monthlySavings = timeSavings * rate * 4.33 * businessMultiplier;
    
    // Additional benefits based on business type and size
    const efficiencyBonus = customers > 50 ? monthlySavings * 0.3 : monthlySavings * 0.2;
    const reviewManagementSavings = customers * 2 * businessMultiplier; // $2 per customer for review responses
    
    const totalMonthlySavings = monthlySavings + efficiencyBonus + reviewManagementSavings;
    const yearlyROI = totalMonthlySavings * 12;

    setCalculatedSavings({
      timeSavings: Math.round(timeSavings * 10) / 10,
      monthlySavings: Math.round(totalMonthlySavings),
      yearlyROI: Math.round(yearlyROI),
      paybackPeriod: Math.ceil(99 / totalMonthlySavings) || 1, // Assuming $99/month cost
      currentCost: Math.round(monthlyLaborCost),
      newCost: Math.round(monthlyLaborCost - monthlySavings),
      efficiency: Math.round((timeSavings / hours) * 100)
    });
    
    setShowResults(true);
  };

  const isFormValid = formData.businessType && formData.customersPerWeek && formData.hoursPerWeek;

  return (
    <Paper
      elevation={3}
      sx={{
        background: 'white',
        p: { xs: 3, sm: 4, md: 5 },
        borderRadius: 4,
        border: '2px solid #e2e8f0',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
      }}
    >
      <Typography
        variant='h5'
        fontWeight={600}
        fontSize={{ xs: 18, sm: 20, md: 24 }}
        sx={{ mb: 2, color: '#1a202c', textAlign: 'center' }}>
        Calculate YOUR Business Savings
      </Typography>
      <Typography
        variant='body1'
        color='text.secondary'
        fontSize={{ xs: 14, sm: 16 }}
        sx={{ 
          mb: { xs: 3, md: 4 }, 
          textAlign: 'center',
          px: { xs: 1, sm: 0 }
        }}>
        Get personalized ROI based on your actual business metrics
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Business Type"
            value={formData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
            variant="outlined"
            size={isMobile ? "medium" : "medium"}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: 14, sm: 16 }
              }
            }}
          >
            {businessTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Customers per week"
            type="number"
            value={formData.customersPerWeek}
            onChange={(e) => handleInputChange('customersPerWeek', e.target.value)}
            variant="outlined"
            placeholder="e.g., 100"
            size={isMobile ? "medium" : "medium"}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: 14, sm: 16 }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Hours spent on customer service/week"
            type="number"
            value={formData.hoursPerWeek}
            onChange={(e) => handleInputChange('hoursPerWeek', e.target.value)}
            variant="outlined"
            placeholder="e.g., 15"
            size={isMobile ? "medium" : "medium"}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: 14, sm: 16 }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Your hourly rate (or staff cost)"
            type="number"
            value={formData.hourlyRate}
            onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
            variant="outlined"
            placeholder="e.g., 25"
            size={isMobile ? "medium" : "medium"}
            InputProps={{
              startAdornment: '$',
            }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: 14, sm: 16 }
              }
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ textAlign: 'center', mt: { xs: 3, md: 4 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={calculateROI}
          disabled={!isFormValid}
          fullWidth={isMobile}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textTransform: "none",
            boxShadow: "none",
            px: { xs: 3, sm: 4 },
            py: { xs: 1.5, sm: 2 },
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 600,
            borderRadius: 3,
            maxWidth: { xs: "100%", sm: 400, md: "auto" },
            '&:hover': {
              transform: "translateY(-2px)",
              boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)"
            }
          }}
        >
          Calculate My Savings
        </Button>
      </Box>

      {showResults && (
        <Box sx={{ mt: { xs: 4, md: 5 } }}>
          <Divider sx={{ mb: { xs: 3, md: 4 } }} />
          
          <Typography
            variant='h6'
            fontWeight={600}
            fontSize={{ xs: 16, sm: 18, md: 20 }}
            sx={{ mb: { xs: 2, md: 3 }, color: '#1a202c', textAlign: 'center' }}>
            🎉 Your Personalized AI ROI
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 1.5, sm: 2 }, 
                background: '#f8fafc', 
                borderRadius: 2,
                minHeight: { xs: 80, sm: 100 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  color="success.main"
                  fontSize={{ xs: 18, sm: 24, md: 28 }}
                >
                  {calculatedSavings.timeSavings}hrs
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  fontSize={{ xs: 11, sm: 12, md: 14 }}
                >
                  Weekly time saved
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 1.5, sm: 2 }, 
                background: '#f8fafc', 
                borderRadius: 2,
                minHeight: { xs: 80, sm: 100 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  color="primary.main"
                  fontSize={{ xs: 18, sm: 24, md: 28 }}
                >
                  ${calculatedSavings.monthlySavings?.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  fontSize={{ xs: 11, sm: 12, md: 14 }}
                >
                  Monthly savings
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 1.5, sm: 2 }, 
                background: '#f8fafc', 
                borderRadius: 2,
                minHeight: { xs: 80, sm: 100 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  color="warning.main"
                  fontSize={{ xs: 18, sm: 24, md: 28 }}
                >
                  ${calculatedSavings.yearlyROI?.toLocaleString()}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  fontSize={{ xs: 11, sm: 12, md: 14 }}
                >
                  Annual ROI
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 1.5, sm: 2 }, 
                background: '#f8fafc', 
                borderRadius: 2,
                minHeight: { xs: 80, sm: 100 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  color="secondary.main"
                  fontSize={{ xs: 18, sm: 24, md: 28 }}
                >
                  {calculatedSavings.paybackPeriod} mo
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  fontSize={{ xs: 11, sm: 12, md: 14 }}
                >
                  Payback period
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: { xs: 3, md: 4 }, 
            p: { xs: 3, sm: 4 }, 
            background: 'linear-gradient(135deg, #28A745, #20c997)', 
            borderRadius: 3, 
            color: 'white', 
            textAlign: 'center' 
          }}>
            <Typography 
              variant="h6" 
              fontWeight={600} 
              fontSize={{ xs: 16, sm: 18, md: 20 }}
              sx={{ mb: 1 }}
            >
              Ready to save ${calculatedSavings.monthlySavings?.toLocaleString()} per month?
            </Typography>
            <Typography 
              variant="body1" 
              fontSize={{ xs: 14, sm: 16 }}
              sx={{ mb: { xs: 2, md: 3 }, opacity: 0.9, px: { xs: 1, sm: 0 } }}
            >
              Join 1M+ businesses already using AI to reduce costs and improve efficiency
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth={isMobile}
              sx={{
                background: 'white',
                color: '#28A745',
                textTransform: "none",
                fontWeight: 600,
                fontSize: { xs: 14, sm: 16 },
                py: { xs: 1.5, sm: 2 },
                maxWidth: { xs: "100%", sm: 300, md: "auto" },
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              Start My AI Upgrade
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default function Stats() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 12 },
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      }}>
      <Container>
        {/* Section Header */}
        <Stack
          mb={{ xs: 4, sm: 5, md: 6 }}
          spacing={2}
          alignItems='center'
          justifyContent='center'>
          <Chip
            label='📊 Real Results'
            sx={{
              color: 'primary.main',
              bgcolor: 'rgba(0, 85, 255, 0.12)',
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 600,
              px: { xs: 1.5, sm: 2 },
            }}
          />
          <Typography
            variant='h2'
            fontWeight={700}
            textAlign='center'
            fontSize={{ xs: 24, sm: 28, md: 32 }}
            color='#1a202c'
            sx={{ px: { xs: 2, sm: 0 } }}>
            Proven Business Impact
          </Typography>
          <Typography
            variant='body1'
            fontSize={{ xs: 14, sm: 16 }}
            textAlign='center'
            color='#64748b'
            sx={{ 
              maxWidth: '500px', 
              mx: 'auto',
              px: { xs: 2, sm: 1, md: 0 }
            }}>
            See how AI transforms businesses like yours
          </Typography>
        </Stack>

        {/* Industry Average Stats - Mobile Responsive Grid */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 6, md: 8 } }}>
          {businessStatsData.map((item) => (
            <Grid key={item.id} item xs={6} sm={6} md={3}>
              <BusinessStatCard
                icon={item.icon}
                value={item.value}
                symbol={item.symbol}
                sign={item.sign}
                title={item.title}
                subtitle={item.subtitle}
                description={item.description}
                iconBg={item.iconBg}
                numberColor={item.numberColor}
                trend={item.trend}
              />
            </Grid>
          ))}
        </Grid>

        {/* Personalized ROI Calculator */}
        <ROICalculator />

        {/* Industry Comparison - Mobile Optimized */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: { xs: 4, sm: 5 },
            borderRadius: 4,
            textAlign: 'center',
            mt: { xs: 5, md: 6 }
          }}
        >
          <Typography
            variant='h5'
            fontWeight={600}
            fontSize={{ xs: 18, sm: 20, md: 24 }}
            sx={{ mb: 1 }}>
            Beat Your Competition
          </Typography>
          <Typography
            variant='body1'
            fontSize={{ xs: 14, sm: 16 }}
            sx={{ 
              opacity: 0.9, 
              mb: { xs: 3, md: 4 }, 
              maxWidth: '600px', 
              mx: 'auto',
              px: { xs: 1, sm: 0 }
            }}>
            See how AI gives you the edge over competitors still doing things manually
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 2, sm: 2 }, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                minHeight: { xs: 100, sm: 120 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h6" 
                  fontWeight={700}
                  fontSize={{ xs: 14, sm: 16, md: 18 }}
                >
                  Response Time
                </Typography>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  fontSize={{ xs: 20, sm: 24, md: 28 }}
                  sx={{ my: 1 }}
                >
                  2 min vs 4 hrs
                </Typography>
                <Typography 
                  variant="body2" 
                  fontSize={{ xs: 12, sm: 14 }}
                  sx={{ opacity: 0.8 }}
                >
                  You vs Industry Average
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 2, sm: 2 }, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                minHeight: { xs: 100, sm: 120 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h6" 
                  fontWeight={700}
                  fontSize={{ xs: 14, sm: 16, md: 18 }}
                >
                  Availability
                </Typography>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  fontSize={{ xs: 20, sm: 24, md: 28 }}
                  sx={{ my: 1 }}
                >
                  24/7 vs 8-5
                </Typography>
                <Typography 
                  variant="body2" 
                  fontSize={{ xs: 12, sm: 14 }}
                  sx={{ opacity: 0.8 }}
                >
                  You vs Competitors
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ 
                textAlign: 'center', 
                p: { xs: 2, sm: 2 }, 
                background: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                minHeight: { xs: 100, sm: 120 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h6" 
                  fontWeight={700}
                  fontSize={{ xs: 14, sm: 16, md: 18 }}
                >
                  Customer Satisfaction
                </Typography>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  fontSize={{ xs: 20, sm: 24, md: 28 }}
                  sx={{ my: 1 }}
                >
                  94% vs 73%
                </Typography>
                <Typography 
                  variant="body2" 
                  fontSize={{ xs: 12, sm: 14 }}
                  sx={{ opacity: 0.8 }}
                >
                  You vs Market Average
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

const businessStatsData = [
  {
    id: 1,
    icon: '⏰',
    value: 7.5,
    symbol: 'hrs',
    sign: '',
    title: 'Time Saved',
    subtitle: 'Average per week',
    description: 'Industry average across all business types',
    iconBg: 'rgba(40, 167, 69, 0.12)',
    numberColor: '#28A745',
    trend: 'up'
  },
  {
    id: 2,
    icon: '🤖',
    value: 85,
    symbol: '%',
    sign: '',
    title: 'Questions Automated',
    subtitle: 'Customer inquiries handled',
    description: 'Across 1M+ businesses using AI',
    iconBg: 'rgba(0, 85, 255, 0.12)',
    numberColor: '#0055FF',
    trend: 'up'
  },
  {
    id: 3,
    icon: '💰',
    value: 67,
    symbol: '%',
    sign: '',
    title: 'Cost Reduction',
    subtitle: 'Vs manual processes',
    description: 'Average across small businesses',
    iconBg: 'rgba(255, 107, 53, 0.12)',
    numberColor: '#FF6B35',
    trend: 'up'
  },
  {
    id: 4,
    icon: '📈',
    value: 94,
    symbol: '%',
    sign: '',
    title: 'Satisfaction Rate',
    subtitle: 'Customer satisfaction',
    description: 'With AI-powered responses',
    iconBg: 'rgba(156, 39, 176, 0.12)',
    numberColor: '#9C27B0',
    trend: 'up'
  }
];